import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import Typography from "@mui/material/Typography"
import { ChevronLeftOutlined } from "@mui-symbols-material/w300"
import { Link, Outlet } from "@tanstack/react-router"
import type { FC, ReactNode } from "react"

import type { GoodsReceiptDetailsTabConfig } from "../types"

import { GoodsReceiptDetailsHeader } from "./GoodsReceiptDetails"

import type { GoodsReceipt } from "@/graphql/codegen/graphql"

interface GoodsReceiptDetailsLayoutProps {
  goodsReceipt: GoodsReceipt
  tabs: GoodsReceiptDetailsTabConfig[]
  activeTab: GoodsReceiptDetailsTabConfig
  backToPath: string
  headerActions?: ReactNode
  children?: ReactNode
  dataTestId: string
  palletsCount: number | null
}

export const GoodsReceiptDetailsLayout: FC<GoodsReceiptDetailsLayoutProps> = ({
  goodsReceipt,
  tabs,
  activeTab,
  backToPath,
  headerActions,
  children,
  dataTestId,
  palletsCount,
}) => {
  return (
    <Paper
      elevation={0}
      className='flex min-h-0 grow flex-col overflow-hidden rounded-lg bg-white'
      data-testid={dataTestId}
    >
      <Box className='mb-6 flex items-center justify-between'>
        <Box className='flex w-full items-center gap-4'>
          <Link to={backToPath}>
            <IconButton aria-label='back to list' className='rounded bg-gray-200 text-primary'>
              <ChevronLeftOutlined />
            </IconButton>
          </Link>
          <Typography
            variant='h5'
            color='primary'
            fontWeight='normal'
            component='h1'
            className='mr-auto'
            data-testid='goods-receipt-title'
          >
            Receipt Details
          </Typography>
          {headerActions}
        </Box>
      </Box>
      <GoodsReceiptDetailsHeader goodsReceipt={goodsReceipt} className='mb-4' palletsCount={palletsCount} />
      <Box className='flex w-full flex-1 flex-col overflow-hidden'>
        <Tabs value={activeTab.path} variant='scrollable' scrollButtons='auto'>
          {tabs
            .filter((tab) => !tab.isHidden)
            .map((tab) => (
              <Tab key={tab.path} label={tab.label} value={tab.path} component={Link} to={tab.path} />
            ))}
        </Tabs>
        <Box className='flex min-h-0 grow flex-col px-1 pb-1 pt-4'>
          <Outlet />
        </Box>
      </Box>
      {children}
    </Paper>
  )
}
